import { createStore, applyMiddleware, compose, combineReducers } from "redux";
import { thunk } from "redux-thunk";

import { CLEAR_STORE } from "./types/auth.types";

import main from "./reducers/main.reducer";
import message from "./reducers/message.reducer";
import auth from "./reducers/auth.reducer";
import inventory from "./reducers/inventory.reducer";
import update from "./reducers/updates.reducer";
import help_center from "./reducers/help_center.reducer";
import orders from "./reducers/orders.reducer";
import maintainers_products from "./reducers/maintainers/product.reducer";
import maintainers_business from "./reducers/maintainers/business.reducer";
import maintainers_users from "./reducers/maintainers/users.reducer";
import maintainers_operators from "./reducers/maintainers/operators.reducer";
import maintainers_species from "./reducers/maintainers/species.reducer";
import maintainers_varieties from "./reducers/maintainers/varieties.reducer";
import maintainers_quarters from "./reducers/maintainers/quarters.reducer";
import maintainers_business_center from "./reducers/maintainers/business_center.reducer";
import maintainers_incidents from "./reducers/maintainers/incidents.reducer";
import maintainers_dealers from "./reducers/maintainers/dealers.reducer";
import maintainers_balances from "./reducers/maintainers/balances.reducer";

const PERSISTED_REDUX_SLICE_KEYS = ["auth", "user", "profile", "main"];
const GLOBAL_STATE_STORAGE_KEY = "GLOBAL_STATE";

const reducer = combineReducers({
	main,
	message,
	auth,
	inventory,
	update,
	help_center,
	orders,
	maintainers_products,
	maintainers_business,
	maintainers_users,
	maintainers_operators,
	maintainers_species,
	maintainers_varieties,
	maintainers_quarters,
	maintainers_business_center,
	maintainers_incidents,
	maintainers_dealers,
	maintainers_balances,
});

export const HYDRATE_STATE = "@@store/HYDRATE_STATE";

function mergePersistedSlices(currentState, persistedSlices) {
	const mergedState = { ...currentState };

	for (const sliceKey of PERSISTED_REDUX_SLICE_KEYS) {
		if (persistedSlices[sliceKey] !== undefined) {
			mergedState[sliceKey] = persistedSlices[sliceKey];
		}
	}

	return mergedState;
}

const rootReducer = (state, action) => {
	if (action.type === CLEAR_STORE) return reducer(undefined, action);
	if (action.type === HYDRATE_STATE) return action.payload;
	return reducer(state, action);
};

const composeEnhancers = window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose;

function buildPersistedState(fullState, sliceKeys = PERSISTED_REDUX_SLICE_KEYS) {
	const persistedState = {};

	for (const sliceKey of sliceKeys) {
		if (fullState[sliceKey] !== undefined) {
			persistedState[sliceKey] = fullState[sliceKey];
		}
	}

	return persistedState;
}

function writePersistedState(state) {
	localStorage.setItem(GLOBAL_STATE_STORAGE_KEY, JSON.stringify(state));
}

function readPersistedState() {
	try {
		const globalState = localStorage.getItem(GLOBAL_STATE_STORAGE_KEY);
		if (!globalState) {
			return undefined;
		}

		return buildPersistedState(JSON.parse(globalState));
	} catch (error) {
		localStorage.removeItem(GLOBAL_STATE_STORAGE_KEY);
		return undefined;
	}
}

const initialState = readPersistedState();
const store = createStore(rootReducer, initialState, composeEnhancers(applyMiddleware(thunk)));

let isHydrating = false;
let saveTimeout = null;

export const saveState = () => {
	try {
		writePersistedState(buildPersistedState(store.getState()));
	} catch (error) {
		if (error?.name !== "QuotaExceededError") {
			throw error;
		}

		localStorage.removeItem(GLOBAL_STATE_STORAGE_KEY);

		try {
			writePersistedState(buildPersistedState(store.getState(), ["auth"]));
		} catch (retryError) {
			if (retryError?.name !== "QuotaExceededError") {
				throw retryError;
			}
		}
	}
};

export const flushPersistedState = () => {
	clearTimeout(saveTimeout);
	saveTimeout = null;
	saveState();
};

export const hydrateFromStorage = (serializedState) => {
	if (!serializedState) return;

	let persistedSlices;
	try {
		persistedSlices = JSON.parse(serializedState);
	} catch {
		return;
	}

	isHydrating = true;
	store.dispatch({
		type: HYDRATE_STATE,
		payload: mergePersistedSlices(store.getState(), persistedSlices),
	});
	isHydrating = false;
};

export const setupStatePersistence = () => {
	const unsubscribe = store.subscribe(() => {
		if (isHydrating) return;
		clearTimeout(saveTimeout);
		saveTimeout = setTimeout(saveState, 300);
	});

	return () => {
		unsubscribe();
		clearTimeout(saveTimeout);
	};
};

export default store;
